import React, { useState, useEffect } from 'react';
import httpClient from '../api/axios';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

const MyOrders = () => {
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [orders, setOrders] = useState([]);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info',
  });

  const userId = useSelector((state) => state.auth.userId);

  const navigate = useNavigate();

  // Fetch orders API function
  const fetchOrders = async () => {
    try {
      const response = await httpClient.get('api/my-orders', {
        headers: {
          'user-id': userId,
        },
      });
      setOrders(response.data);
      console.log(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  // Fetch orders on component mount
  useEffect(() => {
    fetchOrders();
  }, []);

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const toggleOrderDetails = (orderId) => {
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'processing':
        return 'bg-yellow-100 text-yellow-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className='min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8'>
      <div className='max-w-4xl mx-auto'>
        <h1 className='text-3xl font-bold mb-8 text-center border-b pb-4 pt-[100px]'>
          My Orders
        </h1>

        {/* Snackbar for notifications */}
        {snackbar.open && (
          <div
            className={`p-4 mb-4 rounded-md ${
              snackbar.severity === 'error'
                ? 'bg-red-50 text-red-800 border border-red-200'
                : snackbar.severity === 'success'
                ? 'bg-green-50 text-green-800 border border-green-200'
                : 'bg-blue-50 text-blue-800 border border-blue-200'
            }`}
          >
            <div className='flex items-center justify-between'>
              <p>{snackbar.message}</p>
              <button
                onClick={handleCloseSnackbar}
                className='text-sm font-medium'
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {orders?.length === 0 ? (
          <div className='text-center py-12'>
            <p className='text-gray-500 mb-4'>
              {error
                ? 'Error fetching orders. Please try again.'
                : "You haven't placed any orders yet."}
            </p>
            <div className='flex justify-center gap-4'>
              {error && (
                <button
                  className='inline-block bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700'
                  onClick={fetchOrders}
                >
                  Retry
                </button>
              )}
              <button
                className='inline-block bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700'
                onClick={() => navigate('/products')}
              >
                Continue Shopping
              </button>
            </div>
          </div>
        ) : (
          <div className='space-y-6'>
            {orders?.map((order) => (
              <div
                key={order?._id}
                className='bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden'
              >
                <div
                  className='p-4 sm:p-6 cursor-pointer hover:bg-gray-50'
                  onClick={() => toggleOrderDetails(order?._id)}
                >
                  <div className='flex flex-wrap items-center justify-between gap-4'>
                    <div className='flex items-center space-x-4'>
                      {order?.userId && order?.userId.profileImg ? (
                        <img
                          src={order?.userId.profileImg}
                          alt='Profile'
                          className='w-10 h-10 rounded-full object-cover'
                        />
                      ) : (
                        <div className='w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center'>
                          <span className='text-gray-500 font-medium'>
                            {order?.userId && order?.userId.email
                              ? order?.userId.email
                                  .charAt(0)
                                  .toUpperCase()
                              : 'U'}
                          </span>
                        </div>
                      )}
                      <div>
                        <p className='text-sm font-medium text-gray-500'>
                          Order #{order?._id.substring(0, 8)}
                        </p>
                        <p className='text-xs text-gray-400 mt-1'>
                          Ordered on {formatDate(order?.createdAt)}
                        </p>
                      </div>
                    </div>
                    <div className='flex items-center gap-4'>
                      <span
                        className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(
                          order?.orderStatus
                        )}`}
                      >
                        {order?.orderStatus}
                      </span>
                      <p className='text-lg font-semibold text-gray-900'>
                        ₹{order?.totalAmount.toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>

                {expandedOrder === order?._id && (
                  <div className='border-t border-gray-200 p-4 sm:p-6'>
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-6 mb-6'>
                      <div>
                        <h3 className='text-sm font-medium text-gray-500 mb-2'>
                          Delivery Details
                        </h3>
                        <p className='text-gray-600'>
                          {order?.deliveryAddress}
                        </p>
                        <p className='text-gray-600 mt-1'>
                          Contact: {order?.contactNumber}
                        </p>
                      </div>

                      <div>
                        <h3 className='text-sm font-medium text-gray-500 mb-2'>
                          Payment Information
                        </h3>
                        <div className='flex justify-between items-center mb-1'>
                          <span>Method:</span>
                          <span className='font-medium'>
                            {order?.paymentMethod}
                          </span>
                        </div>
                        <div className='flex justify-between items-center'>
                          <span>Status:</span>
                          <span
                            className={`px-2 py-1 rounded-full text-sm ${getPaymentStatusColor(
                              order?.paymentStatus
                            )}`}
                          >
                            {order?.paymentStatus}
                          </span>
                        </div>
                      </div>
                    </div>

                    <h3 className='text-sm font-medium text-gray-500 mb-4'>
                      Order Items
                    </h3>
                    <div className='space-y-4 mb-6'>
                      {order?.items &&
                        order?.items.map((item) => {
                          const productData = item?.productId || {};
                          return (
                            <div
                              key={item?._id}
                              className='flex items-start gap-4'
                            >
                              <img
                                src={productData?.productImg}
                                alt={productData?.name || 'Product'}
                                className='w-16 h-16 object-cover rounded-md border border-gray-200'
                              />
                              <div className='flex-1'>
                                <p className='font-medium text-gray-900'>
                                  {productData?.name || 'Product'}
                                </p>
                                <p className='text-sm text-gray-500'>
                                  {item?.quantityKg || 0} kg × ₹
                                  {item?.pricePerKg || 0}/kg
                                </p>
                                <p className='text-sm text-gray-500'>
                                  {productData?.description || ''}
                                </p>
                              </div>
                              <p className='font-medium text-gray-900'>
                                ₹{(item?.totalPrice || 0).toFixed(2)}
                              </p>
                            </div>
                          );
                        })}
                    </div>

                    <div className='mt-6 pt-4 border-t border-gray-200'>
                      <div className='flex justify-between items-center'>
                        <div>
                          <p className='text-sm text-gray-500'>
                            Order ID: {order._id}
                          </p>
                          <p className='text-sm text-gray-500'>
                            Last Updated:{' '}
                            {formatDate(order.updatedAt)}
                          </p>
                        </div>
                        <div className='text-right'>
                          <p className='font-medium text-gray-900'>
                            Total Amount
                          </p>
                          <p className='text-2xl font-bold text-gray-900'>
                            ₹{order.totalAmount.toFixed(2)}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className='mt-6 flex gap-4'>
                      {/* <button
                        className='px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-md'
                        onClick={() =>
                          console.log('Reorder', order._id)
                        }
                      >
                        Reorder
                      </button> */}
                      {order.orderStatus.toLowerCase() !==
                        'delivered' &&
                        order.orderStatus.toLowerCase() !==
                          'cancelled' && (
                          <button
                            className='px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-md'
                            onClick={() =>
                              console.log('Cancel', order._id)
                            }
                          >
                            Cancel Order
                          </button>
                        )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyOrders;
